// The circle's arithmetic (SPEC-redesign §6.3), kept pure so it is tested
// without a browser and read without one either. Nothing here touches the DOM,
// MapLibre or the view state.
import type { RadiusBand } from '../../scripts/build-map-style.ts';

/** `r` is metres, 200 m to 5 km, and the slider never leaves that range. */
export const MIN_RADIUS = 200;
export const MAX_RADIUS = 5000;

/**
 * Metres per pixel at zoom 0 on the equator.
 *
 * The number every Web Mercator article quotes — 156543.03392 — is for 256 px
 * tiles. MapLibre GL's zoom is defined on 512 px tiles, so a pixel at a given
 * zoom covers half as much ground and the constant is half as large. Using the
 * 256 px value derives a zoom one level too high, which drew the basemap and
 * the marks at DOUBLE scale while the rings and the rim, computed from the
 * radius directly, stayed true: a vehicle 883 m away sat 254 px from the centre
 * of a 287 px disc where 1000 m is 143,5 px.
 *
 * This is the one place it is written down. `scripts/map-look/shoot.ts` calls
 * `zoomFor` rather than keeping a second copy.
 */
const EQUATOR_M_PER_PX = 78271.51696;

/**
 * Zoom is derived, never chosen, so the disc's diameter is exactly twice the
 * radius: a ring at half the disc is half the radius, or the circle means
 * nothing (§6.3). `disc` is the disc's pixel diameter — S3 moves it with the
 * grip and the desktop breakpoint makes it 700, so it is measured, not assumed.
 */
export function zoomFor(radius: number, disc: number, lat: number): number {
  const metresPerPixel = radius / (disc / 2);
  return Math.log2((EQUATOR_M_PER_PX * Math.cos((lat * Math.PI) / 180)) / metresPerPixel);
}

/**
 * The exact inverse of {@link zoomFor}: what radius a camera zoom MEANS.
 *
 * „Zoom IS radius" (SPEC-viewfinder §1.2) — there is no camera zoom independent
 * of `r`, so a pinch is read back through this and written as a radius, and the
 * zoom is then re-derived from that radius by `zoomFor`. Rounded and clamped
 * exactly as `radiusForSlider` is, because a pinch and the slider must be able
 * to produce the same numbers: `r` is 200 m–5 km at a 10 m step (ADR-0009).
 */
export function radiusForZoom(zoom: number, disc: number, lat: number): number {
  const metresPerPixel = (EQUATOR_M_PER_PX * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;
  const metres = metresPerPixel * (disc / 2);
  return Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, Math.round(metres / 10) * 10));
}

/**
 * The ladder rings are chosen from, verbatim from the prototype's own source
 * (`nice`, app.js:297). It has no 750 and it does have 4000 — SPEC-redesign
 * §6.3 listed it the other way round and this PR corrects that subsection.
 */
export const RING_LADDER = [100, 200, 300, 500, 1000, 1500, 2000, 3000, 4000, 5000] as const;

/**
 * The next rung DOWN the ring ladder — what a double tap does (SPEC-viewfinder
 * §1.2, the owner's ruling 10, re-ruled 2026-09-21).
 *
 * The ladder is {@link RING_LADDER} itself, not the rings `ringsFor` happens to
 * draw: which of them are drawn depends on the disc's pixel size, and a gesture
 * whose step changed with the breakpoint is the objection §1.3 makes to
 * `maxBounds`, in a second place. A rung is a rung on every screen.
 *
 * The first ruling was one step of the slider, which measured 0 m below about
 * 1,5 km — finer than the 10 m `r` itself carries (ADR-0009), so the gesture
 * did nothing across half the range. The owner re-ruled on that measurement.
 *
 * At the bottom nothing happens: 100 is on the ladder but below `MIN_RADIUS`,
 * so 200 m is the last rung and a double tap there returns it unchanged.
 */
export function ringBelow(radius: number): number {
  const rungs = RING_LADDER.filter((rung) => rung >= MIN_RADIUS && rung < radius);
  return rungs.at(-1) ?? Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, radius));
}

/**
 * Two rings closer than this read as one band, so the outer one is dropped.
 *
 * Measured on the ring's **diameter**, not its radius — `px = r * k * 2` in the
 * prototype. Reading it as a radius is what made a 287 px disc carry one ring
 * where the prototype carries two to four, and it is the whole of that defect.
 */
const MIN_RING_SPACING_PX = 56;

/**
 * A ring is only drawn well inside the rim. The rim already says „this is the
 * radius", so the prototype keeps the outermost ring at 88 % of it rather than
 * letting one sit on top of the edge.
 */
const RING_CEILING = 0.88;

/**
 * The rings for one radius on one disc, ported from the prototype's source
 * (app.js:297-298) rather than reverse-engineered from what it draws:
 *
 *   const nice = LADDER.filter(r => r <= radius * 0.88);
 *   let lastPx = 0;
 *   for (const r of nice) { const px = r * k * 2; if (px - lastPx >= 56) … }
 *
 * where `k = (disc / 2) / radius`, so `r * k * 2` is the ring's diameter in
 * pixels. The 56 px floor is a gap between two circles as the eye sees them,
 * which is a diameter apart, not a radius apart.
 *
 * Locked by a table of the eight outputs measured by hand in the running
 * prototype at a 287 px disc, so a later tidy-up cannot quietly change it.
 */
export function ringsFor(radius: number, disc: number): number[] {
  const perMetre = disc / 2 / radius;
  const rings: number[] = [];
  let lastPx = 0;
  for (const ring of RING_LADDER) {
    if (ring > radius * RING_CEILING) break;
    const px = ring * perMetre * 2;
    if (px - lastPx < MIN_RING_SPACING_PX) continue;
    rings.push(ring);
    lastPx = px;
  }
  return rings;
}

/**
 * The slider is a native `input[type=range]` over 0…1000 mapped logarithmically
 * onto 200 m…5 km, as the prototype has it, so a step near the bottom of the
 * range moves tens of metres and a step near the top moves hundreds.
 */
export const SLIDER_STEPS = 1000;

/** Rounded to 10 m, because that is the precision `r` carries in the URL (§4.2). */
export function radiusForSlider(position: number): number {
  const t = Math.min(Math.max(position, 0), SLIDER_STEPS) / SLIDER_STEPS;
  const metres = MIN_RADIUS * (MAX_RADIUS / MIN_RADIUS) ** t;
  return Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, Math.round(metres / 10) * 10));
}

/** The inverse, for putting the slider where a pasted link says it should be. */
export function sliderForRadius(radius: number): number {
  const clamped = Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, radius));
  const t = Math.log(clamped / MIN_RADIUS) / Math.log(MAX_RADIUS / MIN_RADIUS);
  return Math.round(t * SLIDER_STEPS);
}

/**
 * Whether a basemap layer is drawn at this radius (§6.2). The bands are in the
 * style JSON's own metadata rather than in a second list here, so the rule and
 * the layer it governs cannot drift apart. See the header of
 * `scripts/build-map-style.ts` for why this cannot be a zoom stop.
 */
export function bandShows(band: RadiusBand | null, radius: number): boolean {
  if (!band) return true;
  if (band.min != null && radius < band.min) return false;
  if (band.max != null && radius > band.max) return false;
  return true;
}

/**
 * A tag's own box, measured off the rendered thing: a `PL` or `AQ` tag is
 * 21,9 × 16,4 px at the sizes §5.6 gives it. Rounded up, because a threshold
 * that merges a shade too eagerly costs one count and a threshold that merges
 * too late costs a legible disc.
 *
 * Two tags merge when their boxes would overlap or touch, which for two equal
 * boxes centred on their anchors is exactly this much apart on each axis. A
 * single radius could not say it: a tag is a third wider than it is tall, so
 * two side by side collide at a distance that two stacked vertically do not.
 */
const TAG_WIDTH_PX = 22;
const TAG_HEIGHT_PX = 17;

/**
 * A mark's anchor on the disc, with whatever the caller needs to draw it back.
 *
 * `w` and `h` are the drawn box, and they default to a tag's. A badge is not a
 * tag: „174 ↑" is twice a tag's width and a third taller, and one threshold for
 * both either left badges overlapping or merged tags that never touched.
 */
export type Anchor<T> = { item: T; voice: string; x: number; y: number; w?: number; h?: number };

/** One drawn tag: the first of its cluster, and everything it stands for. */
export type Cluster<T> = { item: T; voice: string; x: number; y: number; members: T[] };

/**
 * Tags of the same voice whose boxes would overlap or touch become one tag at
 * the first one's anchor, carrying the count („PL 3"). Order is the caller's —
 * axis order — so the one that is drawn, and the one a tap selects, is the
 * first on the axis.
 *
 * A chain counts as one cluster: a is near b and b is near c puts all three
 * together even when a and c are further apart than the threshold, because
 * what the eye sees is one smudge, not two overlapping pairs. Different voices
 * never merge — `PL` and `AQ` mean different things and a count across them
 * would mean nothing.
 *
 * Badges pass one voice for all of them: two lines standing in the same place
 * are one smudge exactly as two events are, and the resident's own vehicles are
 * what #177 measured overlapping by 2,6 px.
 */
export function clusterAnchors<T>(anchors: Anchor<T>[]): Cluster<T>[] {
  const clusters: Cluster<T>[] = [];
  const taken = new Set<number>();

  anchors.forEach((anchor, index) => {
    if (taken.has(index)) return;
    taken.add(index);
    const cluster: Cluster<T> = { ...anchor, members: [anchor.item] };
    // Grown from the front: every member's neighbourhood is searched in turn,
    // so a chain of three is one cluster and not a pair plus a stray.
    const frontier: Anchor<T>[] = [anchor];
    while (frontier.length > 0) {
      const from = frontier.pop();
      if (!from) break;
      anchors.forEach((other, otherIndex) => {
        if (taken.has(otherIndex) || other.voice !== anchor.voice) return;
        // Box overlap, not centre distance: two tags a tag's width apart are
        // touching, and drawing both put „PL" through „PL 3" on a real disc.
        // Two boxes centred on their anchors touch at half of each box's span,
        // which for two equal tags is the tag's own width — the number this
        // read before a badge, which is wider, had to be measured too.
        if (Math.abs(other.x - from.x) > ((from.w ?? TAG_WIDTH_PX) + (other.w ?? TAG_WIDTH_PX)) / 2) return;
        if (Math.abs(other.y - from.y) > ((from.h ?? TAG_HEIGHT_PX) + (other.h ?? TAG_HEIGHT_PX)) / 2) return;
        taken.add(otherIndex);
        cluster.members.push(other.item);
        frontier.push(other);
      });
    }
    clusters.push(cluster);
  });

  return clusters;
}

/** „3 wydarzenia w tym miejscu" — the Polish plural the prototype uses. */
export function clusterCountLabel(count: number, one: string, few: string, many: string): string {
  if (count === 1) return one;
  const last = count % 10;
  const teens = count % 100;
  return last >= 2 && last <= 4 && (teens < 10 || teens >= 20) ? few : many;
}
